import { Link, useLocation } from 'react-router-dom'
import { useState, useEffect } from 'react'
import {
  LayoutDashboard, Mail, Bot, MessageSquare, CheckSquare, PhoneCall, Calendar,
} from 'lucide-react'
import { useI18n } from '../../contexts/I18nContext'
import { useMessagesUnread } from '../../contexts/MessagesUnreadContext'
import { useJanetQueueCount } from '../../hooks/useJanetQueue'
import { supabase } from '../../lib/supabase'
import { BLOCK_NAV, BLOCKS } from '../../config/themes'
import useShellPreview from '../shell/shellPreview'

const TAB_ICONS = {
  '/': LayoutDashboard,
  '/inbox': Mail,
  '/janet/queue': Bot,
  '/messages': MessageSquare,
  '/tasks': CheckSquare,
  '/comms': PhoneCall,
  '/calendar': Calendar,
}

// The always-visible top row: Universal's tabs (Dashboard, Inbox, Messages,
// Tasks, Comms, Calendar) reachable from any Block. These are the outside-world
// communication surfaces, so they never hide behind a rail entry — the left rail
// only carries the record Blocks. Desktop only; mobile keeps Universal in its
// bottom nav.
export default function UniversalBar() {
  const { t } = useI18n()
  const location = useLocation()
  const { unread: messagesUnread } = useMessagesUnread()
  const { pending: janetPending } = useJanetQueueCount()
  const [inboxBadge, setInboxBadge] = useState(0)

  useEffect(() => {
    supabase
      .from('email_intelligence')
      .select('id', { count: 'estimated', head: true })
      .eq('urgency', 'respond_today')
      .in('status', ['analyzed', 'reviewed'])
      .then(({ count }) => setInboxBadge(count || 0))
      .catch(() => {})
  }, [location.pathname])

  const shellPreview = useShellPreview()
  // Shared shell (preview): the Janet chat tab duplicates the persistent rail.
  const tabs = shellPreview
    ? BLOCK_NAV.universal.tabs.filter(tab => tab.path !== '/janet/chat')
    : BLOCK_NAV.universal.tabs
  const gradient = BLOCKS.universal.barGradient

  const tabIsActive = (path) => {
    if (path === '/') return location.pathname === '/'
    return location.pathname === path || location.pathname.startsWith(path + '/')
  }

  const badgeFor = (path) => {
    if (path === '/inbox') return inboxBadge
    if (path === '/janet/queue') return janetPending
    if (path === '/messages') return messagesUnread
    return 0
  }

  return (
    <div
      data-shell="universal-bar"
      className="flex items-center gap-1 px-2.5 py-1 overflow-x-auto shrink-0"
      style={{ background: gradient }}
    >
      {tabs.map(tab => {
        const path = tab.path.split('?')[0]
        const active = tabIsActive(path)
        const Icon = TAB_ICONS[path]
        const badge = badgeFor(path)
        return (
          <Link
            key={tab.path}
            to={tab.path}
            data-active={active || undefined}
            className="relative inline-flex items-center gap-1.5 whitespace-nowrap px-2.5 py-1 rounded-md text-xs font-semibold transition-colors"
            style={{
              color: '#FFFFFF',
              background: active ? 'rgba(255,255,255,0.26)' : 'transparent',
              opacity: active ? 1 : 0.88,
              boxShadow: active ? 'inset 0 0 0 1px rgba(255,255,255,0.35)' : 'none',
            }}
            onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'rgba(255,255,255,0.14)' }}
            onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent' }}
          >
            {Icon && <Icon size={14} />}
            {t(tab.labelKey)}
            {badge > 0 && (
              <span className="text-[9px] font-bold text-white bg-black/25 px-1.5 py-0.5 rounded-full min-w-[16px] text-center leading-none">
                {badge > 99 ? '99+' : badge}
              </span>
            )}
          </Link>
        )
      })}
    </div>
  )
}
